import React, { useState, useEffect, useRef } from 'react';
import { Event } from '../types/event';
import styles from './EventCard.module.css';
import { IconHeart, IconBookmark, IconCalendar, IconMapPin, IconCheck } from '@tabler/icons-react';
import { apiClient } from '../api/apiClient'; 
import { getSavedCount, getInterestCount, getViewCount } from '../utils/eventCounters';

// Set this to true only when debugging is needed
const DEBUG = false;

interface EventCardProps {
  event: Event;
  onClick?: () => void;
  onInterestClick: (eventId: number) => void;
  onSaveClick: (eventId: number) => void;
  isOrganizationDashboard?: boolean;
  distanceLabel?: string; // Optional distance label to display
  className?: string;
  showStatus?: boolean;
}

const EventCard: React.FC<EventCardProps> = ({ 
  event, 
  onClick, 
  onInterestClick, 
  onSaveClick, 
  isOrganizationDashboard = false,
  distanceLabel,
  className = '',
  showStatus = false
}) => {
  const [isInterested, setIsInterested] = useState(Boolean(event.isInterested));
  const [isSaved, setIsSaved] = useState(Boolean(event.isSaved));
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLongTitle, setIsLongTitle] = useState(false);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const [categoryColor, setCategoryColor] = useState<string>('#adb5bd');

  useEffect(() => {
    const boolIsInterested = Boolean(event.isInterested);
    const boolIsSaved = Boolean(event.isSaved);
    
    if (DEBUG) {
      console.log('EventCard - event status:', event.id, 'isInterested:', boolIsInterested, 'isSaved:', boolIsSaved);
    }
    
    // Afficher tous les compteurs possibles pour débogage
    if (DEBUG) {
      console.log('EventCard - compteurs standardisés pour', event.id, {
        interests: getInterestCount(event),
        savedBy: getSavedCount(event),
        views: getViewCount(event)
      });
    }
    
    // Afficher les distances si elles sont disponibles
    if (distanceLabel && DEBUG) {
      console.log(`🔍 EventCard #${event.id} - distanceLabel reçu:`, distanceLabel);
    }
    
    if (boolIsInterested !== isInterested) {
      setIsInterested(boolIsInterested);
    }
    
    if (boolIsSaved !== isSaved) {
      setIsSaved(boolIsSaved);
    }
  }, [event, event.isInterested, event.isSaved, distanceLabel]);

  useEffect(() => {
    // Récupérer la couleur de la catégorie
    if (event) {
      // Si eventCategory est fourni directement par le backend, l'utiliser
      if (event.eventCategory) {
        setCategoryColor(event.eventCategory.color || '#adb5bd');
      } 
      // Sinon, utiliser une couleur par défaut
      else {
        setCategoryColor('#adb5bd');
      }
    }
  }, [event]);

  useEffect(() => {
    if (titleRef.current) {
      setIsLongTitle(event.title.length > 40);
    }
  }, [event.title]);

  const date = new Date(event.dateTime);
  const formattedDate = date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  const formattedTime = date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', hour12: false });

  const truncatedLocation = event.location.length > 25 
    ? `${event.location.substring(0, 22)}...` 
    : event.location;

  const logJwtToken = () => {
    const token = localStorage.getItem('token');
    if (DEBUG) {
      console.log('JWT Token:', token);
    }
    
    try {
      const userId = apiClient.getUserId();
      
      if (userId) {
        if (DEBUG) {
          console.log('User ID extrait avec succès:', userId);
        }
      } else {
        if (DEBUG) {
          console.error('ERREUR: Impossible d\'extraire l\'ID utilisateur du token JWT');
        }
      }
    } catch (error) {
      if (DEBUG) {
        console.error('Erreur lors de l\'utilisation de apiClient:', error);
      }
    }
    
    if (token) {
      const tokenParts = token.split('.');
      if (tokenParts.length === 3) {
        try {
          const header = JSON.parse(atob(tokenParts[0]));
          const payload = JSON.parse(atob(tokenParts[1]));
          if (DEBUG) {
            console.log('Token Header:', header);
            console.log('Token Payload:', payload);
            console.log('User ID dans le token:', payload.sub || payload.id || payload.userId);
            
            const userId = payload.sub || payload.id || payload.userId;
            if (!userId) {
              if (DEBUG) {
                console.error('AUCUN ID utilisateur trouvé dans le token! Vérifiez la génération du JWT.');
              }
            } else {
              if (DEBUG) {
                console.log('Type de User ID:', typeof userId);
              }
            }
          }
        } catch (e) {
          if (DEBUG) {
            console.error('Erreur lors du décodage du token:', e);
          }
        }
      }
    } else {
      if (DEBUG) {
        console.log('Aucun token trouvé dans le localStorage');
      }
    }
  };

  const handleInterestClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    
    logJwtToken();
    
    try {
      setIsLoading(true);
      setError(null);
      
      setIsInterested(!isInterested);
      onInterestClick(event.id);
    } catch (err) {
      if (DEBUG) {
        console.error('Erreur lors du toggle d\'intérêt:', err);
      }
      setError('Erreur lors de l\'opération. Veuillez réessayer.');
      setIsInterested(isInterested); // Revert to previous state
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    
    try {
      setIsLoading(true);
      setError(null);
      
      setIsSaved(!isSaved);
      onSaveClick(event.id);
    } catch (err) {
      if (DEBUG) {
        console.error('Erreur lors du toggle de sauvegarde:', err);
      }
      setError('Erreur lors de l\'opération. Veuillez réessayer.');
      setIsSaved(isSaved); // Revert to previous state
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <article 
      className={`${styles.card} ${className}`} 
      onClick={onClick}
      style={{
        '--category-color': categoryColor
      } as React.CSSProperties}
    >
      {/* Indicateur de catégorie */}
      <div className={styles.categoryIndicator}></div>
      
      <div className={styles.cardInner}>
        {(isInterested || isSaved || isOrganizationDashboard) && showStatus && (
          <div className={`${styles.statusBadge} ${styles[isInterested ? 'interested' : isSaved ? 'saved' : 'active']} `}>
            {isInterested ? (
              <IconHeart size={20} stroke={1.5} fill="currentColor" />
            ) : isSaved ? (
              <IconBookmark size={20} stroke={1.5} fill="currentColor" />
            ) : isOrganizationDashboard ? (
              <IconCheck size={20} stroke={1.5} />
            ) : null}
          </div>
        )}
        <img
          src={event.imageUrl || 'https://placehold.co/400x280/8b5cf6/ffffff?text=Événement'}
          alt={event.title}
          className={styles.image}
          loading="lazy"
          aria-hidden="true"
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            target.src = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 400 280"><defs><linearGradient id="grad" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" style="stop-color:%234f46e5;stop-opacity:1" /><stop offset="50%" style="stop-color:%236366f1;stop-opacity:1" /><stop offset="100%" style="stop-color:%238b5cf6;stop-opacity:1" /></linearGradient></defs><rect width="400" height="280" fill="url(%23grad)"/><text x="50%" y="50%" font-family="Arial" font-size="24" fill="white" text-anchor="middle" dominant-baseline="middle">' + event.title.charAt(0).toUpperCase() + '</text></svg>';
          }}
        />
        <div className={styles.byOrganization}>
          <span>by</span> {event.organization?.name || "Inconnu"}
        </div>
        <div className={styles.content}>
          <h3 
            ref={titleRef}
            className={`${styles.title} ${isLongTitle ? styles['long-title'] : ''}`} 
            title={event.title}
          >
            {event.title || "Événement sans titre"}
          </h3>
          <div className={styles.info}>
            <p className={styles.dateTime} title={`${formattedDate} à ${formattedTime}`}>
              <IconCalendar size={16} stroke={1.5} aria-hidden="true" />
              <span>{formattedDate} • {formattedTime}</span>
            </p>
            <p className={styles.location} title={event.location}>
              <IconMapPin size={16} stroke={1.5} aria-hidden="true" />
              <span>{truncatedLocation}</span>
              {distanceLabel && (
                <span className={styles.distanceLabel} title={`Distance: ${distanceLabel}`}>{distanceLabel}</span>
              )}
            </p>
          </div>
        </div>
        <div className={styles.actions}>
          <div className={styles.counters}>
            <div className={styles.counterItem} title={`${getInterestCount(event)} personnes intéressées`}>
              <IconHeart size={12} stroke={1.5} />
              <span>{getInterestCount(event)}</span>
            </div>
            <div className={styles.counterItem} title={`${getSavedCount(event)} sauvegardes`}>
              <IconBookmark size={12} stroke={1.5} />
              <span>{getSavedCount(event)}</span>
            </div>
          </div>
          <button
            className={`${styles.actionButton} ${isInterested ? styles.active : ''} ${isLoading ? styles.loading : ''}`}
            onClick={handleInterestClick}
            disabled={isLoading}
            aria-label={isInterested ? "Ne plus être intéressé" : "Être intéressé"}
            aria-pressed={isInterested}
            title={error || `${getInterestCount(event)} personnes intéressées`}
            style={isInterested ? { color: '#0066cc', fontWeight: 'bold' } : {}}
          >
            <IconHeart size={14} fill={isInterested ? "#0066cc" : "none"} />
          </button>
          <button
            className={`${styles.actionButton} ${isSaved ? styles.active : ''} ${isLoading ? styles.loading : ''}`}
            onClick={handleSaveClick}
            disabled={isLoading}
            aria-label={isSaved ? "Retirer des favoris" : "Ajouter aux favoris"}
            aria-pressed={isSaved}
            style={isSaved ? { color: '#0066cc', fontWeight: 'bold' } : {}}
          >
            <IconBookmark size={14} fill={isSaved ? "#0066cc" : "none"} />
          </button>
        </div>
      </div>
    </article>
  );
};

export default EventCard;